import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import Select from 'react-select'
import { useAppServices } from 'hook/services'
import { FiCopy, FiEdit, FiEye, FiTrash2 } from 'react-icons/fi'
import '../assets/css/pages/templates/table.scss'

const DataTable = () => {
  const MySwal = withReactContent(Swal)
  const Service = useAppServices()
  const { company_id, app_id } = useParams()
  const navigate = useNavigate()

  const [tableData, setTableData] = useState([])
  const [locations, setLocations] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLocationsLoading, setIsLocationsLoading] = useState(true)

  const searchParams = new URLSearchParams(window.location.search)
  const app_type = searchParams.get('type')

  const getForms = async () => {
    setIsLoading(true)
    try {
      const { response } = await Service.servey.get_levelupstore_forms({
        query: `company_id=${company_id}&app_id=${app_id}`,
      })

      if (response?.data) {
        setTableData(response.data)
      }
    } catch (error) {
      console.error('Error fetching forms:', error)
      Swal.fire({
        icon: 'error',
        title: 'Failed to fetch data',
        text: 'Please try again later',
        showConfirmButton: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getlocations = async () => {
    setIsLocationsLoading(true)
    try {
      const { response } = await Service.servey.get_location({
        query: `company_id=${company_id}&app_id=${
          app_type === 'app_creator' ? '683052a256e6dd0c2f4c2ba7' : '683ed93a8ded1a4103d118a2'
        }`,
      })

      if (response?.data) {
        setLocations(response.data)
      }
    } catch (error) {
      console.error('Error fetching locations:', error)
      Swal.fire({
        icon: 'error',
        title: 'Failed to fetch locations',
        text: 'Please try again later',
        showConfirmButton: true,
      })
    } finally {
      setIsLocationsLoading(false)
    }
  }

  useEffect(() => {
    getForms()
    getlocations()
  }, [])

  const handleEdit = (id) => {
    navigate(`./${id}`, { state: id })
  }

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { response } = await Service.servey.delete_form({
            query: `_id=${id}`,
          })

          if (response.data) {
            Swal.fire({
              icon: 'success',
              title: 'Deleted Successfully',
              showConfirmButton: false,
              timer: 1500,
            })
            getForms()
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Failed to Delete',
              showConfirmButton: false,
              timer: 1500,
            })
          }
        } catch (error) {
          console.error('Error deleting form:', error)
          Swal.fire({
            icon: 'error',
            title: 'Failed to Delete',
            text: 'Please try again later',
            showConfirmButton: true,
          })
        }
      }
    })
  }
  const showMessage = (message: any) => {
    MySwal.fire({
      title: message,
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 3000,
      showCloseButton: true,
    })
  }
  const handleCopyScript = (url: string) => {
    const text = `${url}`

    // Create a temporary textarea element to hold the text
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'

    // Append the textarea to the body
    document.body.appendChild(textarea)

    // Select the text inside the textarea
    textarea.select()

    // Execute the copy command
    document.execCommand('copy')
    showMessage('Copied Successfully')

    // Remove the textarea from the DOM
    document.body.removeChild(textarea)
  }
  const handleCreate = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setNewName('')
    setSelectedLocation(null)
  }

  const handleSave = async () => {
    if (!newName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Name is required',
        showConfirmButton: false,
        timer: 1500,
      })
      return
    }

    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Please select a location',
        showConfirmButton: false,
        timer: 1500,
      })
      return
    }

    try {
      // Default form configuration
      const defaultFormConfig = {
        title: newName.trim(),
        description: 'Complete all steps to submit your information',
        primaryColor: '#3b82f6',
        secondaryColor: '#6b7280',
        fontColor: '#374151',
        backgroundColor: '#f8fafc',
        cardBackgroundColor: '#ffffff',
        completedColor: '#10b981',
        borderColor: '#e5e7eb',
        buttonBackgroundColor: '#3b82f6',
        buttonTextColor: '#ffffff',
        buttonHoverColor: '#2563eb',
        stepBackgroundColor: '#1e293b',
        stepTitleColor: '#ffffff',
        stepDescriptionColor: '#cbd5e1',
        stepIconBackground: '#3b82f6',
        stepNumberColor: '#ffffff',
        fontFamily: 'Inter, sans-serif',
        titleFontSize: 24,
        bodyFontSize: 14,
        labelFontSize: 14,
        buttonFontSize: 14,
        stepSpacing: 24,
        stepPortionWidth: 100,
        iconSize: 20,
        borderRadius: 8,
        borderWidth: 1,
        buttonBorderRadius: 6,
        cardPadding: 24,
        fieldSpacing: 16,
        buttonPadding: 12,
        buttonBorderWidth: 1,
        buttonBorderColor: '#3b82f6',
        steps: [
          {
            id: 1,
            title: 'Personal Information',
            description: 'Tell us about yourself',
            icon: 'FaUser',
            fields: [
              {
                id: '1-1',
                type: 'text',
                customValue: '',
                label: 'Full Name',
                placeholder: 'Enter your full name',
                required: true,
              },
              {
                id: '1-2',
                type: 'email',
                customValue: '',
                label: 'Email Address',
                placeholder: 'Enter your email',
                required: true,
              },
            ],
          },
          {
            id: 2,
            title: 'Contact Details',
            description: 'How can we reach you?',
            icon: 'FaPhone',
            fields: [
              {
                id: '2-1',
                type: 'text',
                customValue: '',
                label: 'Phone Number',
                placeholder: 'Enter your phone number',
                required: false,
              },
              {
                id: '2-2',
                type: 'text',
                customValue: '',
                label: 'Address',
                placeholder: 'Enter your address',
                required: false,
              },
            ],
          },
        ],
      }

      const payload = {
        name: newName.trim(),
        company_id,
        app_id,
        location_id: selectedLocation.value,
        design: defaultFormConfig,
      }

      const { response } = await Service.servey.levelupstore({ payload })

      if (response) {
        Swal.fire({
          icon: 'success',
          title: 'Form Created Successfully',
          showConfirmButton: false,
          timer: 1500,
        })
        getForms()
        handleCloseModal()
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Create Form',
          showConfirmButton: false,
          timer: 1500,
        })
      }
    } catch (error) {
      console.error('Error creating form:', error)
      Swal.fire({
        icon: 'error',
        title: 'Failed to Create Form',
        text: 'Please try again later',
        showConfirmButton: true,
      })
    }
  }

  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: location.name,
  }))

  // Custom Loader Components
  const PulseLoader = () => (
    <div className="pulse-loader">
      <div className="pulse-dot" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="pulse-dot" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="pulse-dot" style={{ backgroundColor: '#2563EB' }}></div>
    </div>
  )

  const WaveLoader = () => (
    <div className="wave-loader">
      <div className="wave-bar" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="wave-bar" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="wave-bar" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="wave-bar" style={{ backgroundColor: '#2563EB' }}></div>
      <div className="wave-bar" style={{ backgroundColor: '#2563EB' }}></div>
    </div>
  )

  return (
    <div id="DataTable" className="data-table-container">
      <div className="data-table-content">
        <div className="header-section">
          <h1 className="page-title">Forms Management</h1>
          <button onClick={handleCreate} className="create-btn">
            <span className="btn-icon">+</span>
            Create New Form
          </button>
        </div>

        <div className="table-container">
          {isLoading ? (
            <div className="loading-state">
              <PulseLoader />
              {/* <p className="loading-text">Loading your forms...</p> */}
            </div>
          ) : tableData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-illustration">
                <svg
                  width="150"
                  height="150"
                  viewBox="0 0 500 500"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M333.333 437.5H166.667C145.667 437.5 128.5 420.333 128.5 399.333V100.667C128.5 79.6667 145.667 62.5 166.667 62.5H333.333C354.333 62.5 371.5 79.6667 371.5 100.667V399.333C371.5 420.333 354.333 437.5 333.333 437.5Z"
                    stroke="#2563EB"
                    strokeWidth="20"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M291.5 54.5V87.1667C291.5 95.5 285.5 101.5 277.167 101.5H222.833C214.5 101.5 208.5 95.5 208.5 87.1667V54.5"
                    stroke="#2563EB"
                    strokeWidth="20"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M208.5 187.5H291.5"
                    stroke="#2563EB"
                    strokeWidth="20"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M208.5 250H291.5"
                    stroke="#2563EB"
                    strokeWidth="20"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M208.5 312.5H250"
                    stroke="#2563EB"
                    strokeWidth="20"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p>No forms found. Create your first form to get started.</p>
              <button onClick={handleCreate} className="create-btn empty-btn">
                <span className="btn-icon">+</span>
                Create Your First Form
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th className="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => (
                  <tr key={row._id}>
                    <td className="id-cell" title={row._id}>
                      {row._id}
                    </td>
                    <td className="name-cell">{row.name}</td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(row._id)}
                          className="icon-btn edit-btn"
                          title="Edit form"
                        >
                          <FiEdit />
                          <span className="tooltip">Edit</span>
                        </button>
                        <button
                          onClick={() =>
                            navigate(
                              `/levelupstore/${company_id}/${app_id}/form_builder/${row._id}/preview?showBack=true`
                            )
                          }
                          className="icon-btn preview-btn"
                          title="Preview form"
                        >
                          <FiEye />
                          <span className="tooltip">Preview</span>
                        </button>
                        <button
                          onClick={() => handleCopyScript(row.url)}
                          className="icon-btn copy-btn"
                          title="Copy form link"
                        >
                          <FiCopy />
                          <span className="tooltip">Copy</span>
                        </button>
                        <button
                          onClick={() => handleDelete(row._id)}
                          className="icon-btn delete-btn"
                          title="Delete form"
                        >
                          <FiTrash2 />
                          <span className="tooltip">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Form</h3>
              <button onClick={handleCloseModal} className="modal-close-btn">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <input
                  id="name-input"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="ENTER FORM NAME"
                  className="name-input"
                  autoFocus
                />
              </div>
              <div className="form-group">
                {isLocationsLoading ? (
                  <div className="locations-loader">
                    <WaveLoader />
                    {/* <p className="loading-text">Loading locations...</p> */}
                  </div>
                ) : (
                  <Select
                    options={locationOptions}
                    value={selectedLocation}
                    onChange={(option) => setSelectedLocation(option)}
                    placeholder="-- Select Location --"
                    className="location-select"
                    classNamePrefix="react-select"
                    isClearable
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSave} className="save-btn">
                Create Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataTable
